// src/pages/CredentialRequests.js
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, formatDate, timeUntil, deletedLabel } from '../utils/api';

function StatusBadge({ status }) {
  const map = {
    pending:   ['status-active',             '● Pending'],
    fulfilled: ['status-expired-downloaded', '✓ Completed'],
    expired:   ['status-expired-timeout',    '⏱ Expired'],
    deleted:   ['status-deleted',            '🗑 Deleted'],
  };
  const [cls, label] = map[status] || ['status-deleted', status];
  return <span className={`status-badge ${cls}`}>{label}</span>;
}

function ViewCredentialModal({ request, onClose }) {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    apiFetch(`requests/${request.id}/download`)
      .then(d => { setContent(d.content || ''); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [request.id]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: '520px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>🔐 View Credentials — {request.title}</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          {loading ? <div style={{ textAlign: 'center', padding: '32px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : error ? <div className="alert alert-error">❌ {error}</div>
          : (
            <>
              <div style={{ fontSize: '13px', color: 'var(--g600)', marginBottom: '12px' }}>Submitted by <strong>{request.recipientEmail}</strong> on {formatDate(request.fulfilledAt)}</div>
              <div style={{ background: 'var(--g900)', padding: '16px', marginBottom: '12px' }}>
                <pre style={{ color: '#00ff88', fontFamily: 'var(--font-mono)', fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>{content}</pre>
              </div>
              <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => { navigator.clipboard.writeText(content); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
                {copied ? '✅ Copied!' : '📋 Copy to clipboard'}
              </button>
            </>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

function RequestCard({ request, onRefresh, showToast }) {
  const [busy, setBusy] = useState('');
  const [modal, setModal] = useState(null);
  const isPending   = request.status === 'pending';
  const isFulfilled = request.status === 'fulfilled';
  const isExpired   = request.status === 'expired';

  const action = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); await onRefresh(); showToast(msg); }
    catch (e) { showToast(`❌ ${e.message}`); }
    finally { setBusy(''); }
  };

  return (
    <div className={`transfer-card${!isPending && !isFulfilled ? ' expired' : ''}`} style={{ display: 'block', padding: 0 }}>
      <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid var(--g100)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px', flexWrap: 'wrap' }}>
          <div className="transfer-title">🔐 {request.title}</div>
          <StatusBadge status={request.status} />
        </div>
        <div className="transfer-meta">
          <span>→ {request.recipientEmail}</span>
          <span>📅 {formatDate(request.createdAt)}</span>
          {isPending && <span style={{ color: 'var(--green)', fontWeight: 600 }}>⏱ {timeUntil(request.expiresAt)}</span>}
          {isFulfilled && <span style={{ color: 'var(--green)' }}>✓ Submitted {formatDate(request.fulfilledAt)}</span>}
          {request.status === 'deleted' && <span style={{ color: 'var(--red)' }}>🗑 {deletedLabel(request)}</span>}
          {request.message && <span style={{ color: 'var(--g500)', fontStyle: 'italic' }}>"{request.message}"</span>}
        </div>
      </div>
      <div style={{ padding: '10px 18px', background: 'var(--g50)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {isFulfilled && (
          <button className="btn btn-primary btn-sm" onClick={() => setModal('view')} disabled={!!busy}>👁 View credentials</button>
        )}
        {isPending && (
          <>
            <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => action('extend', () => apiFetch(`requests/${request.id}/extend`, { method: 'PUT', body: '{}' }), '✅ Extended 3 days')}>⏱ Extend 3 days</button>
            <button className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }} disabled={!!busy}
              onClick={() => action('expire', () => apiFetch(`requests/${request.id}/expire`, { method: 'PUT', body: '{}' }), '✅ Cancelled')}>✕ Cancel</button>
          </>
        )}
        {isExpired && (
          <>
            <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => action('extend', () => apiFetch(`requests/${request.id}/extend`, { method: 'PUT', body: '{}' }), '✅ Re-sent')}>🔄 Re-activate (3 days)</button>
            <button className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }} disabled={!!busy}
              onClick={() => { if (!window.confirm('Delete this request?')) return; action('delete', () => apiFetch(`requests/${request.id}`, { method: 'DELETE', body: '{}' }), '✅ Deleted'); }}>🗑 Delete</button>
          </>
        )}
      </div>
      {modal === 'view' && <ViewCredentialModal request={request} onClose={() => setModal(null)} />}
    </div>
  );
}

export default function CredentialRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await apiFetch('requests/list?type=credential'); setRequests(d.requests || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };
  const pending   = requests.filter(r => r.status === 'pending');
  const fulfilled = requests.filter(r => r.status === 'fulfilled');
  const deleted   = requests.filter(r => r.status === 'deleted');
  const expired   = requests.filter(r => !['pending','fulfilled','deleted'].includes(r.status));
  const shown = tab === 'pending' ? pending : tab === 'fulfilled' ? fulfilled : tab === 'deleted' ? deleted : expired;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <div className="page-eyebrow">Credentials</div>
          <h1 className="page-title">🔓 Credential Requests</h1>
          <p className="page-subtitle">Request passwords, API keys and sensitive text from external parties — single-use secure link</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/credentialrequests/new')}>🔐 New Credential Request</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card stat-card-red"><div className="stat-icon stat-icon-red">🔐</div><div className="stat-info"><div className="stat-value">{requests.length}</div><div className="stat-label">Total</div></div></div>
        <div className="stat-card stat-card-green"><div className="stat-icon stat-icon-green">●</div><div className="stat-info"><div className="stat-value">{pending.length}</div><div className="stat-label">Pending</div></div></div>
        <div className="stat-card stat-card-blue"><div className="stat-icon stat-icon-blue">✓</div><div className="stat-info"><div className="stat-value">{fulfilled.length}</div><div className="stat-label">Completed</div></div></div>
        <div className="stat-card stat-card-amber"><div className="stat-icon stat-icon-amber">⏱</div><div className="stat-info"><div className="stat-value">{expired.length}</div><div className="stat-label">Expired</div></div></div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
            <button className={`tab${tab === 'pending' ? ' active' : ''}`} onClick={() => setTab('pending')}>Pending <span className="tab-badge">{pending.length}</span></button>
            <button className={`tab${tab === 'fulfilled' ? ' active' : ''}`} onClick={() => setTab('fulfilled')}>Completed <span className="tab-badge">{fulfilled.length}</span></button>
            <button className={`tab${tab === 'expired' ? ' active' : ''}`} onClick={() => setTab('expired')}>Expired <span className="tab-badge">{expired.length}</span></button>
            <button className={`tab${tab === 'deleted' ? ' active' : ''}`} onClick={() => setTab('deleted')}>Deleted <span className="tab-badge">{deleted.length}</span></button>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={load}>↻ Refresh</button>
        </div>
        <div className="card-body">
          {loading ? <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : shown.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🔐</div>
              <div className="empty-state-title">{tab === 'pending' ? 'No pending requests' : tab === 'fulfilled' ? 'No completed requests yet' : tab === 'deleted' ? 'No deleted requests' : 'No expired requests'}</div>
              {tab === 'pending' && <><div className="empty-state-desc" style={{ marginBottom: '12px' }}>Send a secure submission link to an external party.</div><button className="btn btn-primary" onClick={() => navigate('/credentialrequests/new')}>Create your first request</button></>}
            </div>
          ) : (
            <div className="transfer-list">
              {shown.map(r => <RequestCard key={r.id} request={r} onRefresh={load} showToast={showToast} />)}
            </div>
          )}
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
